import React, { useRef, useState } from 'react';
import { Box, Center, Spinner, VStack, ButtonIcon, ButtonText, HStack, Icon, Input, InputField, InputSlot, ScrollView, Text, View, } from '@gluestack-ui/themed';
import { ButtonAsync } from '../../primitives';
import { Icons } from '../../icons';
import { Platform } from 'react-native';
// admin project does not play well with the following package, so adding in checks to ensure it does not try to call it
let KeyboardAwareScrollView;
if (Platform.OS !== 'web') {
    KeyboardAwareScrollView =
        require('react-native-keyboard-aware-scroll-view')?.KeyboardAwareScrollView;
}
export const Messages = ({ messages, sendMessage, isPending, sendIsPending, isSyncing, fetchError, sendError, containerProps, messagesContainerProps, displayTimeThresholdInMinutes = 5, messageBubbleColor = '#E9E9EB', backgroundColor = '$gray100', hideScrollbar = true, }) => {
    const [messageInputValue, setMessageInputValue] = useState('');
    const scrollViewRef = useRef();
    const MessagesContainer = Platform.OS === 'web' ? View : KeyboardAwareScrollView;
    const send = async () => {
        try {
            if (messageInputValue.length > 0) {
                await sendMessage(messageInputValue);
                if (scrollViewRef?.current) {
                    // @ts-ignore - no time to define the ref type
                    scrollViewRef.current.scrollToEnd({ animated: true });
                }
                setMessageInputValue('');
            }
        }
        catch (e) {
            console.error('Error sending message: ', e);
        }
    };
    if (fetchError) {
        return (React.createElement(VStack, { w: "$full", flex: 1, gap: "$6", ...containerProps },
            React.createElement(Center, { flex: 1, gap: "$6" },
                React.createElement(Icon, { as: Icons.AlertCircle, w: "$12", h: "$12", color: "$error500" }),
                React.createElement(Text, { size: "lg", color: "$error500" }, "Error fetching messages"))));
    }
    return (React.createElement(MessagesContainer, { style: { flex: 1 } },
        React.createElement(VStack, { w: "$full", flex: 1, gap: "$6", ...containerProps },
            !!isSyncing && (React.createElement(Box, { position: "absolute", top: 0, right: 0, zIndex: 11, opacity: 0.4 },
                React.createElement(Spinner, { color: "$gray400", bg: "$white", p: "$1", borderRadius: "$full" }))),
            isPending ? (React.createElement(Center, { flex: 1 },
                React.createElement(Spinner, null))) : !!messages && messages?.length > 0 ? (React.createElement(ScrollView
            // @ts-ignore - not sure why it doesn't think this prop exists
            , { 
                // @ts-ignore - not sure why it doesn't think this prop exists
                ref: scrollViewRef, flex: 1, showsVerticalScrollIndicator: !hideScrollbar, onContentSizeChange: () => scrollViewRef?.current &&
                    // @ts-ignore - no time to define the ref type
                    scrollViewRef.current.scrollToEnd({ animated: true }) },
                React.createElement(VStack, { flex: 1, space: "lg", ...messagesContainerProps }, messages.map((messageData, index) => {
                    const showTime = index === 0 ||
                        new Date(messageData.createdAt).valueOf() -
                            new Date(messages[index - 1].createdAt).valueOf() >
                            1000 * 60 * displayTimeThresholdInMinutes;
                    return (React.createElement(React.Fragment, { key: `message:${messageData.sender}:${messageData.createdAt}` },
                        showTime && (React.createElement(Center, null,
                            React.createElement(Text, { size: "lg", color: "$textSubtle", mt: index > 0 ? '$12' : '$2' }, formatMessageTimestamp(messageData.createdAt)))),
                        React.createElement(Message, { ...messageData, showSenderName: !messageData.isCurrentUser, bubbleColor: messageBubbleColor, backgroundColor: backgroundColor })));
                })))) : (React.createElement(Center, { flex: 1, opacity: 0.3 },
                React.createElement(VStack, { space: "xl", alignItems: "center" },
                    React.createElement(Icon, { as: Icons.MessageCircle, w: "$28", h: "$28", color: "$textSubtle" }),
                    React.createElement(Text, { color: "$textSubtle", size: "lg" }, "No messages yet")))),
            React.createElement(VStack, { w: "$full", space: "sm" },
                React.createElement(Input, { bg: "$white", sx: {
                        borderWidth: 1,
                        borderColor: '$gray300',
                        _web: {
                            boxShadow: 'none',
                            ':focus': {
                                boxShadow: 'none',
                            },
                        },
                    }, isDisabled: !!isPending },
                    React.createElement(InputField, { size: "lg", value: messageInputValue, onChangeText: (newTextVal) => setMessageInputValue(newTextVal), onKeyPress: (e) => {
                            if (e.nativeEvent?.key === 'Enter') {
                                send();
                            }
                        }, sx: { borderWidth: 0 }, placeholder: "Message", borderColor: "$red500" }),
                    React.createElement(InputSlot, { pr: "$2", m: 0 },
                        React.createElement(Center, null,
                            React.createElement(ButtonAsync, { onPress: send, pending: !!sendIsPending, isDisabled: messageInputValue.length === 0 || !!isPending, 
                                // @ts-ignore - not sure why it doesn't like this
                                xs: {
                                    ':disabled': {
                                        opacity: 0.5,
                                    },
                                }, bg: "transparent", minWidth: "auto", p: "$0.5", height: "auto", spinnerProps: {
                                    color: '$primary500',
                                } },
                                React.createElement(ButtonText, { color: "$textSubtle" }, "Send"),
                                React.createElement(ButtonIcon, { size: "lg", color: "$textSubtle", as: Icons.Send }))))),
                !!sendError && (React.createElement(Text, { color: "$error500" }, "There was an error sending this message!"))))));
};
const Message = ({ sender, message, isCurrentUser, showSenderName = true, bubbleColor = '#E9E9EB', backgroundColor = '$gray100', }) => {
    const [textHeight, setTextHeight] = useState(0);
    const minHeight = 44;
    return (React.createElement(HStack, { w: "$full", justifyContent: isCurrentUser ? 'flex-end' : 'flex-start', maxHeight: minHeight + textHeight, minHeight: minHeight },
        !isCurrentUser && (React.createElement(MessageTail, { side: "left", color: bubbleColor, maskColor: backgroundColor })),
        React.createElement(VStack, { maxWidth: "$4/5", space: "xs" },
            showSenderName && (React.createElement(Text, { ml: isCurrentUser ? '$2' : '$8', size: "md", color: "$textSubtle" }, sender)),
            React.createElement(VStack, { zIndex: 10, bg: bubbleColor, px: "$6", py: "$3", borderRadius: "$3xl", ml: isCurrentUser ? 0 : '$6', mr: isCurrentUser ? '$6' : 0 },
                React.createElement(Box, null,
                    React.createElement(Text, { size: "lg", onLayout: (event) => {
                            const { height } = event.nativeEvent.layout;
                            setTextHeight(height);
                        } }, message)))),
        isCurrentUser && (React.createElement(MessageTail, { side: "right", color: bubbleColor, maskColor: backgroundColor }))));
};
const MessageTail = ({ side, color, maskColor }) => {
    return (React.createElement(VStack, { h: "$full", justifyContent: "flex-end" },
        React.createElement(Box, { w: "$10", h: "$6", bg: color, position: "absolute", bottom: Platform.OS !== 'web' && side === 'right' ? 12 : undefined, left: side === 'left' ? 0 : undefined, right: side === 'right' ? 0 : undefined, borderBottomRightRadius: side === 'right' ? 0 : 20, borderBottomLeftRadius: side === 'left' ? 0 : 20 }),
        React.createElement(Box, { w: "$6", h: "$6", bg: maskColor, position: "absolute", bottom: Platform.OS !== 'web' && side === 'right' ? 12 : undefined, left: side === 'left' ? 0 : undefined, right: side === 'right' ? 0 : undefined, borderBottomRightRadius: side === 'right' ? 0 : 48, borderBottomLeftRadius: side === 'left' ? 0 : 48 })));
};
const formatMessageTimestamp = (date) => {
    const today = new Date();
    if (typeof date === 'string') {
        date = new Date(date);
    }
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ];
    const splitUnits = getSplitTimestampValue(date);
    const todayUnits = getSplitTimestampValue(today);
    let dateString = null;
    // If the message was sent today or yesterday, display that instead of the full date
    if (splitUnits.year === todayUnits.year &&
        splitUnits.month === todayUnits.month) {
        if (splitUnits.date === todayUnits.date) {
            dateString = 'Today';
        }
        else if (splitUnits.date === todayUnits.date - 1) {
            dateString = 'Yesterday';
        }
    }
    // If still unassigned, just string format the date
    if (dateString === null) {
        const day = days[splitUnits.day];
        const month = months[splitUnits.month];
        const dayOfMonth = date.getDate();
        dateString = `${day}, ${month} ${dayOfMonth}`;
    }
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    return `${dateString} ${hours % 12}:${minutes
        .toString()
        .padStart(2, '0')}${ampm}`;
};
const getSplitTimestampValue = (date) => ({
    year: date.getFullYear(),
    month: date.getMonth(),
    date: date.getDate(),
    day: date.getDay(),
    hours: date.getHours(),
    minutes: date.getMinutes(),
});
